import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'courses';
const ENROLLMENTS_COLLECTION = 'courseEnrollments';
const NOTIFICATIONS_COLLECTION = 'notifications';

export const courseService = {
  /**
   * Get all published and upcoming courses
   * Only returns courses that are open for enrollment
   */
  async getUpcoming(limit = 20) {
    try {
      // Try the indexed query first, fall back to simple query if index doesn't exist
      let snapshot;
      try {
        const q = query(
          collection(db, COLLECTION),
          where('isPublished', '==', true),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (indexError) {
        // Fallback: get all courses if index doesn't exist
        console.warn('[Courses] Firestore index may be missing, fetching all courses:', indexError.message);
        const fallbackQuery = query(collection(db, COLLECTION));
        snapshot = await getDocs(fallbackQuery);
      }

      const now = new Date();
      // Set to start of today for more lenient filtering
      now.setHours(0, 0, 0, 0);

      const allCourses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sessions: (doc.data().sessions || []).map(s => ({
          ...s,
          date: s.date?.toDate?.() || s.date
        })),
        startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
        endDate: doc.data().endDate?.toDate?.() || doc.data().endDate,
        earlyBirdDeadline: doc.data().earlyBirdDeadline?.toDate?.() || doc.data().earlyBirdDeadline
      }));

      // Filter published courses whose endDate has NOT yet passed
      const filtered = allCourses.filter(course => {
        // Must be published
        if (!course.isPublished) {
          return false;
        }

        // Status check
        if (course.status === 'completed' || course.status === 'cancelled') {
          return false;
        }

        // Use endDate if available to determine if course is still upcoming
        if (course.endDate) {
          const endDate = course.endDate instanceof Date ? course.endDate : new Date(course.endDate);
          return endDate >= now;
        }

        // Fallback: check last session date for legacy courses without endDate
        if (!course.sessions || course.sessions.length === 0) {
          return false;
        }

        const lastSession = course.sessions[course.sessions.length - 1];
        const sessionDate = lastSession.date instanceof Date
          ? lastSession.date
          : new Date(lastSession.date);

        return sessionDate >= now;
      });

      return filtered
        .sort((a, b) => {
          // Sort by first session date (ascending - soonest first)
          const dateA = a.sessions[0]?.date instanceof Date ? a.sessions[0].date : new Date(a.sessions[0]?.date || 0);
          const dateB = b.sessions[0]?.date instanceof Date ? b.sessions[0].date : new Date(b.sessions[0]?.date || 0);
          return dateA - dateB;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('[Courses] Error fetching upcoming courses:', error);
      return [];
    }
  },

  /**
   * Get all published courses (including past)
   */
  async getAll(limit = 50) {
    try {
      let snapshot;
      try {
        const q = query(
          collection(db, COLLECTION),
          where('isPublished', '==', true),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (indexError) {
        console.warn('Firestore index may be missing, fetching all courses:', indexError.message);
        const fallbackQuery = query(collection(db, COLLECTION));
        snapshot = await getDocs(fallbackQuery);
      }

      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          sessions: (doc.data().sessions || []).map(s => ({
            ...s,
            date: s.date?.toDate?.() || s.date
          })),
          startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
          endDate: doc.data().endDate?.toDate?.() || doc.data().endDate,
          earlyBirdDeadline: doc.data().earlyBirdDeadline?.toDate?.() || doc.data().earlyBirdDeadline
        }))
        .filter(course => course.isPublished) // Ensure only published courses
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching all courses:', error);
      return [];
    }
  },

  /**
   * Get course by ID
   */
  async getById(id) {
    try {
      const docRef = doc(db, COLLECTION, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          sessions: (data.sessions || []).map(s => ({
            ...s,
            date: s.date?.toDate?.() || s.date
          })),
          startDate: data.startDate?.toDate?.() || data.startDate,
          endDate: data.endDate?.toDate?.() || data.endDate,
          earlyBirdDeadline: data.earlyBirdDeadline?.toDate?.() || data.earlyBirdDeadline
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching course:', error);
      return null;
    }
  },

  /**
   * Get courses by type (course, training, certification)
   */
  async getByType(type, limit = 20) {
    try {
      const q = query(
        collection(db, COLLECTION),
        where('isPublished', '==', true),
        where('type', '==', type),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const now = new Date();

      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          sessions: (doc.data().sessions || []).map(s => ({
            ...s,
            date: s.date?.toDate?.() || s.date
          })),
          startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
          endDate: doc.data().endDate?.toDate?.() || doc.data().endDate,
          earlyBirdDeadline: doc.data().earlyBirdDeadline?.toDate?.() || doc.data().earlyBirdDeadline
        }))
        .filter(course => {
          if (course.status === 'completed' || course.status === 'cancelled') return false;

          // Use endDate if available
          if (course.endDate) {
            const endDate = course.endDate instanceof Date ? course.endDate : new Date(course.endDate);
            return endDate >= now;
          }

          // Fallback to first session date
          if (!course.sessions || course.sessions.length === 0) return false;
          const firstSession = course.sessions[0].date instanceof Date
            ? course.sessions[0].date
            : new Date(course.sessions[0].date);
          return firstSession >= now;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching courses by type:', error);
      return [];
    }
  },

  /**
   * Get courses by category (reiki, meditation, etc.)
   */
  async getByCategory(category, limit = 20) {
    try {
      const q = query(
        collection(db, COLLECTION),
        where('isPublished', '==', true),
        where('category', '==', category),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const now = new Date();

      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          sessions: (doc.data().sessions || []).map(s => ({
            ...s,
            date: s.date?.toDate?.() || s.date
          })),
          startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
          endDate: doc.data().endDate?.toDate?.() || doc.data().endDate,
          earlyBirdDeadline: doc.data().earlyBirdDeadline?.toDate?.() || doc.data().earlyBirdDeadline
        }))
        .filter(course => {
          if (course.status === 'completed' || course.status === 'cancelled') return false;

          // Use endDate if available
          if (course.endDate) {
            const endDate = course.endDate instanceof Date ? course.endDate : new Date(course.endDate);
            return endDate >= now;
          }

          // Fallback to first session date
          if (!course.sessions || course.sessions.length === 0) return false;
          const firstSession = course.sessions[0].date instanceof Date
            ? course.sessions[0].date
            : new Date(course.sessions[0].date);
          return firstSession >= now;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching courses by category:', error);
      return [];
    }
  },

  /**
   * Get past courses (courses that have completed)
   */
  async getPast(limit = 20) {
    try {
      let snapshot;
      try {
        const q = query(
          collection(db, COLLECTION),
          where('isPublished', '==', true),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (indexError) {
        console.warn('Firestore index may be missing, fetching all courses:', indexError.message);
        const fallbackQuery = query(collection(db, COLLECTION));
        snapshot = await getDocs(fallbackQuery);
      }

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          sessions: (doc.data().sessions || []).map(s => ({
            ...s,
            date: s.date?.toDate?.() || s.date
          })),
          startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
          endDate: doc.data().endDate?.toDate?.() || doc.data().endDate,
          earlyBirdDeadline: doc.data().earlyBirdDeadline?.toDate?.() || doc.data().earlyBirdDeadline
        }))
        .filter(course => {
          // Must be published
          if (!course.isPublished) return false;

          // Explicitly completed courses are always past
          if (course.status === 'completed') return true;

          // Use endDate if available - course is past only when endDate has passed
          if (course.endDate) {
            const endDate = course.endDate instanceof Date ? course.endDate : new Date(course.endDate);
            return endDate < now;
          }

          // Fallback: courses with no sessions and no endDate count as past
          if (!course.sessions || course.sessions.length === 0) return true;

          // Fallback: use last session date for legacy courses
          const lastSession = course.sessions[course.sessions.length - 1];
          const sessionDate = lastSession.date instanceof Date
            ? lastSession.date
            : new Date(lastSession.date);
          return sessionDate < now;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching past courses:', error);
      return [];
    }
  },

  /**
   * Enroll in a course
   */
  async enroll(courseId, enrollmentData) {
    try {
      const course = await this.getById(courseId);
      if (!course) {
        throw new Error('Course not found');
      }
      if (course.spots.available <= 0) {
        throw new Error('Course is full');
      }

      // Check if email already enrolled
      const existingQuery = query(
        collection(db, ENROLLMENTS_COLLECTION),
        where('courseId', '==', courseId),
        where('email', '==', enrollmentData.email?.toLowerCase())
      );
      const existingDocs = await getDocs(existingQuery);
      if (!existingDocs.empty) {
        throw new Error('You are already enrolled in this course');
      }

      // Determine price (early bird or regular)
      let finalPrice = course.price;
      let isEarlyBird = false;
      if (course.earlyBirdPrice && course.earlyBirdDeadline) {
        const now = new Date();
        const deadline = course.earlyBirdDeadline instanceof Date
          ? course.earlyBirdDeadline
          : new Date(course.earlyBirdDeadline);
        if (now < deadline) {
          finalPrice = course.earlyBirdPrice;
          isEarlyBird = true;
        }
      }

      const enrollment = {
        courseId,
        courseName: course.title,
        name: enrollmentData.name,
        email: enrollmentData.email?.toLowerCase(),
        phone: enrollmentData.phone,
        notes: enrollmentData.notes || '',

        // Payment info
        pricePaid: finalPrice,
        isEarlyBird,
        paymentStatus: 'pending',

        // Status
        status: 'enrolled',
        attendedSessions: [],

        // Source
        source: 'website',

        enrolledAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, ENROLLMENTS_COLLECTION), enrollment);

      // Update course spots
      const courseDocRef = doc(db, COLLECTION, courseId);
      const newAvailable = course.spots.available - 1;
      let newStatus = course.status;

      if (newAvailable <= 0) {
        newStatus = 'full';
      } else if (newAvailable <= 3) {
        newStatus = 'filling-fast';
      } else if (course.status === 'draft' || course.status === 'published') {
        newStatus = 'open';
      }

      await updateDoc(courseDocRef, {
        'spots.available': newAvailable,
        status: newStatus,
        enrollmentCount: (course.enrollmentCount || 0) + 1,
        updatedAt: serverTimestamp()
      });

      // Create notification for admin
      await this.createEnrollmentNotification(course, {
        ...enrollment,
        id: docRef.id
      });

      return {
        id: docRef.id,
        ...enrollment,
        spotsRemaining: newAvailable,
        isEarlyBird,
        pricePaid: finalPrice
      };
    } catch (error) {
      console.error('Error enrolling in course:', error);
      throw error;
    }
  },

  /**
   * Create notification for admin when someone enrolls
   */
  async createEnrollmentNotification(course, enrollment) {
    try {
      // Format session dates for notification
      const sessionDates = course.sessions?.map(s => {
        const d = s.date instanceof Date ? s.date : new Date(s.date);
        return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
      }).join(', ') || 'TBA';

      const notificationData = {
        type: 'course_enrollment',
        title: 'New Course Enrollment',
        message: `${enrollment.name} enrolled in ${course.title}`,
        courseId: course.id,
        courseTitle: course.title,
        courseType: course.type,
        enrollmentId: enrollment.id,
        enrolleeName: enrollment.name,
        enrolleeEmail: enrollment.email,
        enrolleePhone: enrollment.phone,
        pricePaid: enrollment.pricePaid,
        isEarlyBird: enrollment.isEarlyBird,
        sessionDates: sessionDates,
        spotsRemaining: course.spots.available - 1,
        read: false,
        readBy: [],
        createdAt: serverTimestamp()
      };
      await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
    } catch (err) {
      console.error('Error creating enrollment notification:', err);
    }
  },

  /**
   * Check if a course has available spots
   */
  async hasAvailableSpots(courseId) {
    try {
      const course = await this.getById(courseId);
      return course && course.spots.available > 0;
    } catch (error) {
      console.error('Error checking course availability:', error);
      return false;
    }
  },

  /**
   * Get spots information for a course
   */
  async getSpots(courseId) {
    try {
      const course = await this.getById(courseId);
      if (!course) return null;
      return {
        total: course.spots.total,
        available: course.spots.available,
        taken: course.spots.total - course.spots.available,
        status: course.status
      };
    } catch (error) {
      console.error('Error getting course spots:', error);
      return null;
    }
  },

  /**
   * Get current price for a course (checks early bird deadline)
   */
  getCurrentPrice(course) {
    if (!course) return null;

    if (course.earlyBirdPrice && course.earlyBirdDeadline) {
      const now = new Date();
      const deadline = course.earlyBirdDeadline instanceof Date
        ? course.earlyBirdDeadline
        : new Date(course.earlyBirdDeadline);
      if (now < deadline) {
        return {
          price: course.earlyBirdPrice,
          isEarlyBird: true,
          regularPrice: course.price,
          deadline: deadline
        };
      }
    }

    return {
      price: course.price,
      isEarlyBird: false,
      regularPrice: course.price,
      deadline: null
    };
  }
};

export default courseService;
